import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { CheckIcon } from "lucide-react";
import { mutateRequest, putFetcher, roleLabel, ROLES_LIST } from "@/lib/swr";
import type { User, RoleValue } from "@/lib/swr";
import { useState } from "react";
import useSWRMutation from "swr/mutation";

export const EditRolesDialog = ({ user }: { user: User }) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>(
    user?.roles || []
  );

  const { trigger } = useSWRMutation(`/users/${user.id}`, putFetcher);

  const handleUpdateRoles = async () => {
    trigger(
      {
        roles: selectedRoles,
      },
      {
        onSuccess: () => {
          toast({
            title: "Update success",
            description: "User roles updated",
          });
          // 列表与详情页都从 /users/full 取数,改完全局重拉
          mutateRequest("/users/full");
        },
        onError: (err) => {
          toast({
            title: "Update failed",
            description: err?.message,
          });
        },
      }
    );
  };

  const toggleRole = (role: RoleValue) => {
    setSelectedRoles((current: string[]) => {
      if (current?.includes(role)) {
        return current?.filter((r: string) => r !== role);
      }
      return [...current, role];
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          Edit Roles
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit user roles</DialogTitle>
          <DialogDescription>
            Functional roles for people-picker filtering. Current roles:{" "}
            {user?.roles?.map(roleLabel).join(", ") || "none"}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <Command className="border rounded-lg">
            <CommandInput placeholder="Search role…" />
            <CommandEmpty>No results.</CommandEmpty>
            <CommandList>
              <CommandGroup>
                {ROLES_LIST.map((role) => (
                  <CommandItem
                    key={role}
                    value={`${roleLabel(role)} ${role}`}
                    onSelect={() => toggleRole(role)}
                    className="flex items-center justify-between"
                  >
                    <span>
                      {roleLabel(role)}
                      <span className="ml-1.5 text-xs text-muted-foreground">
                        {role}
                      </span>
                    </span>
                    {selectedRoles?.includes(role) && (
                      <CheckIcon className="w-4 h-4" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
          <Button onClick={handleUpdateRoles}>Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
